import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PotreePointcloud from "components/vr/PotreePointcloud";
import useAllProjectFiles from "hooks/useAllProjectFiles";
import ComparePanel from "components/compare/ComparePanel";
import "./GemeoDigital.css";

const GemeoDigital = ({ email, token }) => {
  const navigate = useNavigate();
  const query = new URLSearchParams(window.location.search);
  const projectName = query.get("projectName");
  const demo = query.get("demo");
  const date = query.get("date");

  const [metadataUrl, setMetadataUrl] = useState(null);
  const [error, setError] = useState(null);

  // Compare state
  const [compareMode, setCompareMode] = useState(false);
  const [compareDateA, setCompareDateA] = useState(0);
  const [compareDateB, setCompareDateB] = useState(1);
  const [compareView, setCompareView] = useState("toggle");
  const [activeCompareDate, setActiveCompareDate] = useState("A");

  // Buscar todas as datas com gemeo_digital
  const { allDates: allGemeoFiles } = useAllProjectFiles(token, projectName, "gemeo_digital");
  const canCompare = allGemeoFiles.length >= 2;

  // Extrair metadata URL de cada date
  const getMetadataUrlFromFiles = (files) => {
    const metaFile = files.find(f => f.url.endsWith("metadata.json"));
    return metaFile ? metaFile.url : null;
  };

  const compareMetadataUrlA = canCompare ? getMetadataUrlFromFiles(allGemeoFiles[compareDateA]?.files || []) : null;
  const compareMetadataUrlB = canCompare ? getMetadataUrlFromFiles(allGemeoFiles[compareDateB]?.files || []) : null;

  useEffect(() => {
    if (!projectName || !date) return;

    const fetchMetadataUrl = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/projects/list_files`,
          {
            folder_path: `projetos/${projectName}/${date}/gemeo_digital`
          },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        const metadataFile = response.data.files.find(file =>
          file.url.endsWith("metadata.json")
        );

        if (metadataFile) {
          setMetadataUrl(metadataFile.url);
        } else {
          setError("Arquivo de metadata não encontrado.");
        }
      } catch (err) {
        setError("Erro ao carregar o metadata.");
      }
    };

    fetchMetadataUrl();
  }, [projectName, date, token]);

  useEffect(() => {
    document.body.style.setProperty("overflow", "hidden", "important");
    return () => {
      document.body.style.setProperty("overflow", "auto", "important");
    };
  }, []);

  if (error) {
    return <p>{error}</p>;
  }

  if (!metadataUrl) {
    return (
      <div className="d-flex justify-content-center mt-60 vh-100 vw-100">
        <div className="spinner-border" role="status">
          <span className="sr-only">Carregando metadata...</span>
        </div>
      </div>
    );
  }

  const folderPath = projectName && date
    ? `projetos/${projectName}/${date}/gemeo_digital`
    : null;

  const handleCloseGemeo = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/projects", {
        state: projectName ? { openProject: projectName } : undefined,
      });
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: "0",
        left: "0",
        right: "0",
        bottom: "0",
        overflow: "hidden",
        zIndex: 1
      }}
    >
      {/* Botao fechar (canto superior direito) */}
      <div className="gemeo-close">
        <button type="button" title="Voltar" onClick={handleCloseGemeo}>&times;</button>
      </div>

      <PotreePointcloud
        metadataUrl={metadataUrl}
        demo={demo}
        folderPath={folderPath}
        compareMode={compareMode}
        compareMetadataUrlA={compareMetadataUrlA}
        compareMetadataUrlB={compareMetadataUrlB}
        compareView={compareView}
        activeCompareDate={activeCompareDate}
        canCompare={canCompare}
        onToggleCompare={() => setCompareMode(prev => !prev)}
      />

      {/* Compare panel overlay */}
      {compareMode && canCompare && (
        <div className="gemeo-compare__overlay">
          <ComparePanel
            items={allGemeoFiles}
            indexA={compareDateA}
            indexB={compareDateB}
            onChangeA={setCompareDateA}
            onChangeB={setCompareDateB}
          >
            <div className="gemeo-compare__view-switch">
              <button
                className={`gemeo-compare__view-btn ${compareView === "toggle" ? "gemeo-compare__view-btn--active" : ""}`}
                onClick={() => setCompareView("toggle")}
              >
                Alternar
              </button>
              <button
                className={`gemeo-compare__view-btn ${compareView === "overlay" ? "gemeo-compare__view-btn--active" : ""}`}
                onClick={() => setCompareView("overlay")}
              >
                Sobrepor
              </button>
            </div>
          </ComparePanel>

          <button
            className="gemeo-compare__exit-btn"
            onClick={() => setCompareMode(false)}
            title="Sair do modo comparação"
          >
            ✕ Sair
          </button>
        </div>
      )}

      {/* Toggle A/B no modo toggle */}
      {compareMode && compareView === "toggle" && (
        <div className="gemeo-compare__toggle-ab">
          <button
            className={`gemeo-compare__ab-btn ${activeCompareDate === "A" ? "gemeo-compare__ab-btn--active" : ""}`}
            onClick={() => setActiveCompareDate("A")}
          >
            A
          </button>
          <button
            className={`gemeo-compare__ab-btn ${activeCompareDate === "B" ? "gemeo-compare__ab-btn--active" : ""}`}
            onClick={() => setActiveCompareDate("B")}
          >
            B
          </button>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  email: state.auth.email,
  token: state.auth.accessToken
});

export default connect(mapStateToProps, null)(GemeoDigital);
